import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Link as LinkIcon, X, Plus } from "lucide-react";
import { dynType } from "@/lib/metaDynamic";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const LINK_TYPES = [
  { id: "blocks", label: "blocks" },
  { id: "is_blocked_by", label: "is blocked by" },
  { id: "relates_to", label: "relates to" },
  { id: "duplicates", label: "duplicates" },
  { id: "depends_on", label: "depends on" },
  { id: "clones", label: "clones" },
];

const READABLE = {
  blocks: "blocks", is_blocked_by: "is blocked by", relates_to: "relates to",
  duplicates: "duplicates", is_duplicated_by: "is duplicated by",
  clones: "clones", is_cloned_by: "is cloned by",
  depends_on: "depends on", required_by: "required by",
};

export default function IssueLinks({ issueId }) {
  const { openIssue } = useApp();
  const [links, setLinks] = useState([]);
  const [adding, setAdding] = useState(false);
  const [linkType, setLinkType] = useState("relates_to");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  const load = async () => {
    if (!issueId) return;
    const r = await api.get(`/issues/${issueId}/links`);
    setLinks(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [issueId]);

  useEffect(() => {
    if (!query) { setResults([]); return; }
    const t = setTimeout(async () => {
      const r = await api.get("/search", { params: { q: query } });
      setResults((r.data.issues || []).filter((i) => i.id !== issueId).slice(0, 6));
    }, 200);
    return () => clearTimeout(t);
  }, [query, issueId]);

  const add = async (target) => {
    try {
      await api.post(`/issues/${issueId}/links`, {
        from_issue_id: issueId, to_issue_id: target.id, link_type: linkType,
      });
      toast.success("Linked");
      setAdding(false); setQuery(""); setResults([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    await api.delete(`/issue-links/${id}`);
    load();
  };

  // group by link type
  const grouped = links.reduce((acc, l) => {
    (acc[l.link_type] = acc[l.link_type] || []).push(l);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Linked issues</div>
        <button className="text-xs text-primary hover:underline flex items-center gap-1" onClick={() => setAdding(!adding)} data-testid="add-link-btn">
          <Plus className="w-3 h-3" /> Add link
        </button>
      </div>
      {adding ? (
        <div className="border border-border rounded-md p-2 mb-2 space-y-2" data-testid="add-link-panel">
          <div className="flex gap-2">
            <Select value={linkType} onValueChange={setLinkType}>
              <SelectTrigger className="w-40 h-8" data-testid="link-type"><SelectValue /></SelectTrigger>
              <SelectContent>{LINK_TYPES.map((l) => (<SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>))}</SelectContent>
            </Select>
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search issue by key or summary…" className="h-8" data-testid="link-search" />
          </div>
          {results.length > 0 ? (
            <div className="border border-border rounded overflow-hidden">
              {results.map((r) => {
                const T = dynType(r.issue_type);
                return (
                  <button key={r.id} onClick={() => add(r)} data-testid={`link-option-${r.key}`} className="w-full text-left flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent transition-colors">
                    <T.icon className={cn("w-4 h-4", T.cls)} />
                    <span className="font-mono text-xs text-muted-foreground">{r.key}</span>
                    <span className="truncate">{r.summary}</span>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
      {links.length === 0 ? (
        <div className="text-xs text-muted-foreground">No links yet.</div>
      ) : (
        <div className="space-y-2">
          {Object.entries(grouped).map(([type, list]) => (
            <div key={type}>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 mb-1 font-medium">{READABLE[type] || type}</div>
              <div className="space-y-1">
                {list.map((l) => {
                  const i = l.issue;
                  if (!i) return null;
                  const T = dynType(i.issue_type);
                  return (
                    <div key={l.id} className="flex items-center gap-2 border border-border rounded px-2 py-1.5 group text-sm" data-testid={`link-row-${l.id}`}>
                      <T.icon className={cn("w-4 h-4", T.cls)} />
                      <button onClick={() => openIssue(i.id)} className="font-mono text-xs text-muted-foreground hover:text-primary">{i.key}</button>
                      <span className="flex-1 truncate">{i.summary}</span>
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border status-${i.status}`}>{i.status}</span>
                      <button onClick={() => del(l.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive" data-testid={`del-link-${l.id}`}><X className="w-3.5 h-3.5" /></button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
